import { getElementMeta } from "./schema";
import { assert, getItemLength, newSelection, strPosToUni, uniToStrPos, } from "./utils";
export const isDOMText = (node) => {
    return (node === null || node === void 0 ? void 0 : node.nodeType) === 3;
};
export const isDOMComment = (node) => {
    return (node === null || node === void 0 ? void 0 : node.nodeType) === 8;
};
export const isDOMElement = (node) => {
    return (node === null || node === void 0 ? void 0 : node.nodeType) === 1;
};
export const cloestElement = (node) => {
    if (node === null)
        return null;
    return isDOMElement(node) ? node : node.parentElement;
};
export const caretPositionFromPoint = (x, y) => {
    if (document.caretPositionFromPoint !== undefined) {
        const domPos = document.caretPositionFromPoint(x, y);
        return domPos && [domPos.offsetNode, domPos.offset];
    }
    const range = document.caretRangeFromPoint(x, y);
    if (range !== null) {
        assert(range.collapsed);
        return [range.startContainer, range.startOffset];
    }
    return null;
};
export class DomStructureGuard {
    constructor(root, filter) {
        this.root = root;
        this.filter = filter;
        this.mutationRecordBuffer = [];
        this.mutationObserver = new MutationObserver((records) => {
            this.mutationRecordBuffer.push(...records);
        });
        this.observing = false;
    }
    start() {
        if (this.observing)
            return;
        this.observing = true;
        this.mutationObserver.observe(this.root, {
            characterData: true,
            characterDataOldValue: true,
            childList: true,
            subtree: true,
        });
    }
    stop() {
        this.mutationObserver.disconnect();
    }
    stopAndRollback() {
        var _a;
        if (!this.observing)
            return;
        this.observing = false;
        this.mutationRecordBuffer.push(...this.mutationObserver.takeRecords());
        this.mutationObserver.disconnect();
        while (this.mutationRecordBuffer.length > 0) {
            const record = this.mutationRecordBuffer.pop();
            if (this.filter !== undefined && !this.filter(record.target))
                continue;
            switch (record.type) {
                case "characterData": {
                    record.target.nodeValue = record.oldValue;
                    break;
                }
                case "childList": {
                    try {
                        for (const node of Array.from(record.removedNodes)) {
                            record.target.insertBefore(node, record.nextSibling);
                        }
                        for (const node of Array.from(record.addedNodes)) {
                            (_a = node.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(node);
                        }
                    }
                    catch (_b) {
                        // 防止报错白屏
                    }
                    break;
                }
            }
        }
    }
}
const MAGIC = "tr40owj";
export const getEditorDataProps = (model, content) => {
    return {
        [`data-${MAGIC}`]: "",
        ["data-editor"]: `${model.editorKey}`,
        ["data-revision"]: `${model.getContentRevision(content)}`,
    };
};
export const getTextDataProps = (length) => {
    return {
        [`data-${MAGIC}`]: "",
        ["data-text"]: "",
        ["data-length"]: `${length}`,
    };
};
export const getCursorStopDataProps = () => {
    return {
        [`data-${MAGIC}`]: "",
        ["data-cursor-stop"]: "",
    };
};
export const getElementDataProps = (element) => {
    return {
        [`data-${MAGIC}`]: "",
        ["data-element"]: "",
        ["data-length"]: `${getItemLength(element)}`,
        ["data-has-boundary"]: getElementMeta(element.tag).hasBoundary
            ? ""
            : undefined,
        ["data-void"]: element.content === null ? "" : undefined,
    };
};
export const findEditorElement = (editorKey) => {
    return document.querySelector(`[data-${MAGIC}][data-editor="${editorKey}"]`);
};
export const lookupEditorElement = (node) => {
    var _a, _b;
    return (_b = (_a = cloestElement(node)) === null || _a === void 0 ? void 0 : _a.closest(`[data-${MAGIC}][data-editor]`)) !== null && _b !== void 0 ? _b : null;
};
export const lookupCursorStopElement = (node) => {
    var _a, _b;
    return ((_b = (_a = cloestElement(node)) === null || _a === void 0 ? void 0 : _a.closest(`[data-${MAGIC}][data-cursor-stop]`)) !== null && _b !== void 0 ? _b : null);
};
export const isEditorActive = (editorElement) => {
    return (document.activeElement !== null &&
        editorElement !== null &&
        editorElement.contains(document.activeElement));
};
export const doesEditorHasFocus = (editorKey) => {
    return (document.querySelector(`[data-${MAGIC}][data-editor="${editorKey}"]:focus-within`) !== null);
};
export const hasRevisionRendered = (editorElement, revision) => {
    var _a;
    return ((_a = (editorElement === null || editorElement === void 0 ? void 0 : editorElement.getAttribute("data-revision")) === `${revision}`) !== null && _a !== void 0 ? _a : false);
};
//#region normalizeDOMPosition
// CREDIT: copid from slatejs
/**
 * Get the nearest editable child and index at `index` in a `parent`, preferring
 * `direction`.
 */
const getEditableChildAndIndex = (parent, index, direction) => {
    const { childNodes } = parent;
    let child = childNodes[index];
    let i = index;
    let triedForward = false;
    let triedBackward = false;
    // While the child is a comment node, or an element node with no children,
    // keep iterating to find a sibling non-void, non-comment node.
    while (isDOMComment(child) ||
        (isDOMElement(child) && child.childNodes.length === 0) ||
        (isDOMElement(child) &&
            child.getAttribute("contenteditable") === "false" &&
            !child.hasAttribute("data-tr40owj"))) {
        if (triedForward && triedBackward) {
            break;
        }
        if (i >= childNodes.length) {
            triedForward = true;
            i = index - 1;
            direction = "backward";
            continue;
        }
        if (i < 0) {
            triedBackward = true;
            i = index + 1;
            direction = "forward";
            continue;
        }
        child = childNodes[i];
        index = i;
        i += direction === "forward" ? 1 : -1;
    }
    return [child, index];
};
/**
 * Get the nearest editable child at `index` in a `parent`, preferring
 * `direction`.
 */
const getEditableChild = (parent, index, direction) => {
    const [child] = getEditableChildAndIndex(parent, index, direction);
    return child;
};
/**
 * Normalize a DOM point so that it always refers to a text node.
 */
export const normalizeDOMPosition = (domPos) => {
    let [node, offset] = domPos;
    // If it's an element node, its offset refers to the index of its children
    // including comment nodes, so try to find the right text child node.
    if (isDOMElement(node) && node.childNodes.length) {
        let isLast = offset === node.childNodes.length;
        let index = isLast ? offset - 1 : offset;
        [node, index] = getEditableChildAndIndex(node, index, isLast ? "backward" : "forward");
        // If the editable child found is in front of input offset, we instead seek to its end
        isLast = index < offset;
        // If the node has children, traverse until we have a leaf node. Leaf nodes
        // can be either text nodes, or other void DOM nodes.
        while (isDOMElement(node) && node.childNodes.length) {
            const i = isLast ? node.childNodes.length - 1 : 0;
            node = getEditableChild(node, i, isLast ? "backward" : "forward");
        }
        // Determine the new offset inside the text node.
        offset = isLast && node.textContent != null ? node.textContent.length : 0;
    }
    // Return the node and offset.
    return [node, offset];
};
//#endregion
const lengthOfElement = (element) => {
    if (element.hasAttribute(`data-${MAGIC}`)) {
        if (element.hasAttribute("data-text") ||
            element.hasAttribute("data-element")) {
            return Number(element.getAttribute("data-length"));
        }
        return 0;
    }
    else {
        let length = 0;
        for (let child = element.firstElementChild; child !== null; child = child.nextElementSibling) {
            length += lengthOfElement(child);
        }
        return length;
    }
};
const distanceToRootStart = (last, root) => {
    assert(root.contains(last));
    let distance = 0;
    let element = last;
    while (element !== null && element !== root) {
        const prevElement = element.previousElementSibling;
        if (prevElement !== null) {
            distance += lengthOfElement(prevElement);
            element = prevElement;
        }
        else {
            element = element.parentElement;
            if (element !== null &&
                element.hasAttribute(`data-${MAGIC}`) &&
                element.hasAttribute("data-element") &&
                element.hasAttribute("data-has-boundary")) {
                distance += 1;
            }
        }
    }
    return distance;
};
export const convertPositionFromDOM = (domPos) => {
    var _a, _b;
    domPos = normalizeDOMPosition(domPos);
    const [container, offset] = domPos;
    const editorElement = lookupEditorElement(container);
    if (editorElement === null)
        return null;
    const editorKey = editorElement.getAttribute("data-editor");
    const element = cloestElement(container);
    const distance = distanceToRootStart(element, editorElement);
    if (element.hasAttribute(`data-${MAGIC}`) &&
        element.hasAttribute("data-text")) {
        return [
            editorKey,
            distance + strPosToUni((_b = (_a = element.firstChild) === null || _a === void 0 ? void 0 : _a.textContent) !== null && _b !== void 0 ? _b : "", offset),
        ];
    }
    else {
        return [editorKey, distance];
    }
};
export const convertPositionToDOM = ([editorKey, offset]) => {
    const editorElement = findEditorElement(editorKey);
    assert(editorElement !== null);
    const iterElements = function* (parent) {
        for (let element = parent.firstElementChild; element !== null; element = element.nextElementSibling) {
            if (element.hasAttribute(`data-${MAGIC}`)) {
                yield element;
            }
            else {
                yield* iterElements(element);
            }
        }
    };
    const find = (parent, offset) => {
        for (const element of iterElements(parent)) {
            if (element.hasAttribute(`data-${MAGIC}`)) {
                if (offset === 0 && element.hasAttribute("data-cursor-stop")) {
                    return [element.firstChild, 1];
                }
                if (element.hasAttribute("data-text")) {
                    const length = Number(element.getAttribute("data-length"));
                    if (offset <= length) {
                        const text = element.firstChild;
                        return [text, uniToStrPos(text.textContent, offset)];
                    }
                    offset -= length;
                }
                else if (element.hasAttribute("data-element")) {
                    const length = Number(element.getAttribute("data-length"));
                    const hasBoundary = element.hasAttribute("data-has-boundary");
                    if (!element.hasAttribute("data-void") &&
                        (hasBoundary ? offset < length : offset <= length)) {
                        return find(element, offset + (hasBoundary ? -1 : 0));
                    }
                    offset -= length;
                }
            }
        }
        assert(false);
    };
    return find(editorElement, offset);
};
export const readSelectionFromDOM = () => {
    const selection = document.getSelection();
    if (selection.focusNode === null)
        return null;
    const anchorPos = convertPositionFromDOM([
        selection.anchorNode,
        selection.anchorOffset,
    ]);
    const focusPos = convertPositionFromDOM([
        selection.focusNode,
        selection.focusOffset,
    ]);
    if (anchorPos === null || focusPos === null)
        return null;
    if (anchorPos[0] !== focusPos[0])
        return null;
    return [focusPos[0], newSelection(anchorPos[1], focusPos[1])];
};
export const writeSelectionToDOM = ([editorKey, selection]) => {
    const domSel = document.getSelection();
    const anchorPos = convertPositionToDOM([editorKey, selection.anchorOffset]);
    const focusPos = convertPositionToDOM([editorKey, selection.focusOffset]);
    if (anchorPos[0] !== domSel.anchorNode ||
        anchorPos[1] !== domSel.anchorOffset ||
        focusPos[0] !== domSel.focusNode ||
        focusPos[1] !== domSel.focusOffset) {
        domSel.setBaseAndExtent(anchorPos[0], anchorPos[1], focusPos[0], focusPos[1]);
    }
};
export const clearDOMSelection = () => {
    const domSel = document.getSelection();
    domSel.removeAllRanges();
};
export const getRenderedRevision = (editorKey) => {
    const editorElement = findEditorElement(editorKey);
    if (editorElement === null)
        return null;
    return Number(editorElement.getAttribute("data-revision"));
};
export const withInsertNodeTemporarily = (container, offset, node, callback) => {
    const root = cloestElement(container);
    const domGuard = new DomStructureGuard(root);
    const range = new Range();
    range.setStart(container, offset);
    range.setEnd(container, offset);
    domGuard.start();
    range.insertNode(node);
    callback();
    domGuard.stopAndRollback();
};
